import React, { useEffect, useMemo, useRef, useState } from 'react';
import EducationalContentViewer from '../coachLed/EducationalContentViewer';
import gql from 'graphql-tag';
import client from '../../lib/apolloClient';

// GraphQL mutations
const CREATE_DOCUMENT = gql`
  mutation CreateDocument($file: File!, $name: String, $category: String, $document_type: Int) {
    createDocument(file: $file, name: $name, category: $category, document_type: $document_type) {
      id
      name
      download_url
      created_at
    }
  }
`;

const UPLOAD_PROOF = gql`
  mutation UploadProof($actionPlanItemId: ID!, $documentId: ID!) {
    upload_proof(action_plan_item_id: $actionPlanItemId, document_id: $documentId) {
      errors
    }
  }
`;

// Props:
// - isOpen: boolean
// - onClose: () => void
// - task: { id, title, description?, status, priority, dueAt, assigneeUserId, domain, taskTemplate?, documents?, coach_review_status? }
// - onSave: (taskId, updates) => Promise<void>
// - onMarkCompleted: (taskId) => Promise<void>
// - onDelete: (taskId) => Promise<void>
// - onTaskUpdated?: () => void  // Callback to refresh task data after upload
// - isCoachView?: boolean  // Whether this is being viewed by a coach
// - onReview?: (taskId, reviewStatus, coachNotes) => Promise<void>  // Callback for coach review (approve/reject)
// - sprintProgramName?: string  // Name of the sprint program (for coach-led programs)
const TaskDetailModal = ({ isOpen, onClose, task, onSave, onMarkCompleted, onDelete, onTaskUpdated, className, isCoachView = false, onReview, sprintProgramName }) => {
  const [edited, setEdited] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const titleRef = useRef(null);
  const [education, setEducation] = useState([]);
  const [coachNotes, setCoachNotes] = useState('');
  const [isReviewing, setIsReviewing] = useState(false);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && task) {
      setEdited({
        title: task.title || '',
        description: task.description || '',
        status: task.status || 'open',
        priority: task.priority || 'medium',
        dueAt: task.dueAt || '',
        assigneeUserId: task.assigneeUserId || '',
      });
      setCoachNotes('');
      setTimeout(() => titleRef.current && titleRef.current.focus(), 100);
      // Load educational content support
      const keys = (task.taskTemplate?.key || task.taskTemplateKey || '').toString();
      try { window.lastTask = task; } catch(_) {}
      console.log('[TaskDetailModal] Task template key:', keys);
      if (keys) {
        const url = `/educational_contents.json?task_key=${encodeURIComponent(keys)}`;
        console.log('[TaskDetailModal] Fetching educational content:', url);
        fetch(url, { credentials: 'same-origin' })
          .then(r => {
            console.log('[TaskDetailModal] Response status:', r.status, r.ok);
            return r.ok ? r.json() : Promise.resolve([]);
          })
          .then(list => {
            console.log('[TaskDetailModal] Raw response:', list);
            const arr = Array.isArray(list) ? list : (Array.isArray(list.educational_contents) ? list.educational_contents : []);
            console.log('[TaskDetailModal] Parsed array:', arr, 'length:', arr.length);
            setEducation(arr);
            try { window.eduDebug = { keys, url, count: arr.length, sample: arr.slice(0,3), fullData: arr }; } catch(_) {}
          })
          .catch((err) => { 
            console.error('[TaskDetailModal] Fetch error:', err);
            setEducation([]); 
            try { window.eduDebug = { keys, error: true, errorDetails: err.message }; } catch(_) {} 
          });
      } else {
        console.log('[TaskDetailModal] No task template key found');
        setEducation([]);
      }
    }
  }, [isOpen, task]);

  const hasChanges = useMemo(() => {
    if (!task || !edited) return false;
    return (
      edited.title !== (task.title || '') ||
      edited.description !== (task.description || '') ||
      edited.status !== (task.status || 'open') ||
      edited.priority !== (task.priority || 'medium') ||
      (edited.dueAt || '') !== (task.dueAt || '') ||
      (edited.assigneeUserId || '') !== (task.assigneeUserId || '')
    );
  }, [task, edited]);

  if (!isOpen || !task || !edited) return null;

  const handleField = (field, value) => {
    setEdited((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);
      await onSave(task.id, edited);
    } catch (e) {
      setError('Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  const handleMarkCompleted = async () => {
    try {
      setIsSaving(true);
      setError(null);
      await onMarkCompleted(task.id);
      onClose();
    } catch (e) {
      setError('Failed to mark as completed');
    } finally {
      setIsSaving(false);
    }
  };

  const handleApprove = async () => {
    if (!onReview) return;
    try {
      setIsReviewing(true);
      setError(null);
      await onReview(task.id, 'approved', null);
      onClose();
    } catch (e) {
      setError('Failed to approve task');
    } finally {
      setIsReviewing(false);
    }
  };

  const handleReject = async () => {
    if (!onReview) return;
    try {
      setIsReviewing(true);
      setError(null);
      await onReview(task.id, 'rejected', coachNotes.trim() || null);
      onClose();
    } catch (e) {
      setError('Failed to reject task');
    } finally {
      setIsReviewing(false);
    }
  };

  // Check if task is pending review
  const isPendingReview = task?.status?.toLowerCase() === 'completed' && 
                          task?.coach_review_status?.toLowerCase() === 'pending';

  const handleDelete = async () => {
    try {
      setIsSaving(true);
      setError(null);
      await onDelete(task.id);
      onClose();
    } catch (e) {
      setError('Failed to delete task');
    } finally {
      setIsSaving(false);
    }
  };

  const handleProofUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    
    // Capture the input element before async operations (React reuses synthetic events)
    const inputElement = e.target;
    
    // Validate file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setError(`File size exceeds 10MB limit. File size: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      
      // 1) Create Document via GraphQL mutation (using Shrine infrastructure)
      // Note: apollo-upload-client expects file as array for File! scalar type
      const docResult = await client.mutate({
        mutation: CREATE_DOCUMENT,
        variables: {
          file: [file],
          name: file.name,
          category: 'other', // String enum value
          document_type: 0, // default_document
        },
        context: { hasUpload: true },
      });

      if (!docResult.data || !docResult.data.createDocument) {
        throw new Error('Failed to create document');
      }

      const documentId = docResult.data.createDocument.id;

      // 2) Link document to ActionPlanItem via GraphQL upload_proof
      const proofResult = await client.mutate({
        mutation: UPLOAD_PROOF,
        variables: {
          actionPlanItemId: task.id,
          documentId: documentId,
        },
      });

      if (proofResult.data?.upload_proof?.errors && proofResult.data.upload_proof.errors.length > 0) {
        throw new Error(proofResult.data.upload_proof.errors.join(', '));
      }

      // Reset file input using captured element
      if (inputElement) {
        inputElement.value = '';
      }

      // Notify parent to refresh task data
      if (onTaskUpdated) {
        onTaskUpdated();
      }

      setError(null);
    } catch (e) {
      console.error('Error uploading proof:', e);
      setError(e.message || 'Failed to upload proof');
    } finally {
      setIsSaving(false);
    }
  };

  const stop = (e) => e.stopPropagation();

  const handleBackdropClick = (e) => {
    // Only close if clicking the backdrop itself, not children
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleBackdropWheel = (e) => {
    // Prevent background scrolling when scrolling on backdrop
    // The modal content itself handles scrolling
    if (e.target === e.currentTarget) {
      e.stopPropagation();
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 p-4 overflow-y-auto"
      onClick={handleBackdropClick}
      onWheel={handleBackdropWheel}
      onTouchMove={handleBackdropWheel}
      style={{ WebkitOverflowScrolling: 'touch' }}
    >
      <div 
        className={`relative bg-succession-bg-primary w-full max-w-5xl rounded-xl shadow-xl border border-succession-border flex flex-col my-4 ${className || ''}`}
        style={{ maxHeight: 'calc(100vh - 2rem)' }}
        onClick={stop}
      >
        {/* Header - fixed */}
        <div className="flex-shrink-0 px-6 py-4 border-b border-succession-border bg-succession-bg-primary flex items-center justify-between">
          <div>
            <div className="text-xs text-succession-text-muted">Task Details</div>
            <div className="text-lg font-semibold text-succession-text-primary truncate">{task.title}</div>
          </div>
          <button className="px-3 py-2 text-sm text-succession-text-primary hover:text-succession-text-secondary" onClick={onClose}>Close</button>
        </div>

        {/* Body - scrollable container */}
        <div className="flex-1 min-h-0 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="flex flex-col md:flex-row">
            {/* Left - editable content */}
            <div className="flex-1 p-6 space-y-5">
              <div className="space-y-2">
                <label className="text-sm text-succession-text-primary">Title *</label>
                <input 
                  ref={titleRef} 
                  className="w-full border border-succession-border bg-succession-bg-primary text-succession-text-primary rounded-md px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed" 
                  value={edited.title} 
                  onChange={(e) => handleField('title', e.target.value)}
                  disabled={isCoachView}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm text-succession-text-primary">Description</label>
                <textarea 
                  className="w-full border border-succession-border bg-succession-bg-primary text-succession-text-primary rounded-md px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed" 
                  rows={5} 
                  value={edited.description} 
                  onChange={(e) => handleField('description', e.target.value)}
                  disabled={isCoachView}
                />
              </div>

              {/* Coach Review Section */}
              {isCoachView && isPendingReview && (
                <div className="space-y-4 p-4 bg-purple-50 dark:bg-purple-950 border border-purple-200 dark:border-purple-800 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-purple-900 dark:text-purple-100">
                      ⏳ Pending Coach Review
                    </span>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm text-purple-900 dark:text-purple-100">
                      Review Notes (optional, shown when rejecting)
                    </label>
                    <textarea
                      className="w-full border border-purple-300 dark:border-purple-700 bg-white dark:bg-purple-900 text-purple-900 dark:text-purple-100 rounded-md px-3 py-2"
                      rows={3}
                      value={coachNotes}
                      onChange={(e) => setCoachNotes(e.target.value)}
                      placeholder="Add notes for the participant (especially if rejecting)..."
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleApprove}
                      disabled={isReviewing}
                      className="flex-1 px-4 py-2 text-sm rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isReviewing ? 'Approving...' : '✓ Approve Task'}
                    </button>
                    <button
                      onClick={handleReject}
                      disabled={isReviewing}
                      className="flex-1 px-4 py-2 text-sm rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isReviewing ? 'Rejecting...' : '✗ Reject & Request Revision'}
                    </button>
                  </div>
                </div>
              )}

              {/* Upload Proof under description */}
              <div className="space-y-2">
                <label className="text-sm text-succession-text-primary">Supporting Documentation</label>
                <div>
                  <label className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-succession-border rounded cursor-pointer text-succession-text-primary hover:bg-succession-hover-bg">
                    Attach Proof
                    <input type="file" className="hidden" onChange={handleProofUpload} disabled={isSaving} />
                  </label>
                </div>
                {/* Display uploaded documents */}
                {task.documents && task.documents.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {task.documents.map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between p-2 bg-succession-bg-secondary rounded border border-succession-border">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-succession-text-primary truncate">{doc.name || 'Unnamed document'}</div>
                          <div className="text-xs text-succession-text-secondary">
                            {doc.created_at ? `Uploaded ${doc.created_at}` : ''}
                          </div>
                        </div>
                        {doc.download_url && (
                          <a
                            href={doc.download_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-2 px-2 py-1 text-xs text-succession-primary hover:underline"
                          >
                            Download
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Educational Content / Learning Resources */}
              {(() => {
                console.log('[TaskDetailModal RENDER] education state:', education, 'length:', education?.length);
                return null;
              })()}
              {education && education.length > 0 && (
                <div className="space-y-2">
                  <label className="text-sm text-succession-text-primary">Learning Resources</label>
                  <EducationalContentViewer
                    content={education.map((ec) => ({
                      id: ec.id,
                      title: ec.title,
                      description: ec.description,
                      locale: ec.locale || 'EN',
                      content_type: ec.content_type,
                      md_body: ec.md_body,
                      embed_code: ec.embed_code,
                      asset_url: ec.asset_url,
                      fileName: ec.fileName,
                      sortOrder: 0, // REST API doesn't provide sortOrder, use 0
                    }))}
                    userLocale={(window.locale || 'en').toUpperCase().substring(0, 2) === 'FR' ? 'FR' : 'EN'}
                  />
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm text-succession-text-primary">Status</label>
                  <select 
                    className="w-full border border-succession-border bg-succession-bg-primary text-succession-text-primary rounded-md px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed" 
                    value={edited.status} 
                    onChange={(e) => handleField('status', e.target.value)}
                    disabled={isCoachView}
                  >
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="blocked">Blocked</option>
                    <option value="completed">Completed</option>
                    <option value="wont_do">Won't Do</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-succession-text-primary">Priority</label>
                  <select 
                    className="w-full border border-succession-border bg-succession-bg-primary text-succession-text-primary rounded-md px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed" 
                    value={edited.priority} 
                    onChange={(e) => handleField('priority', e.target.value)}
                    disabled={isCoachView}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-succession-text-primary">Due Date</label>
                  <input 
                    type="date" 
                    className="w-full border border-succession-border bg-succession-bg-primary text-succession-text-primary rounded-md px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed" 
                    value={edited.dueAt ? new Date(edited.dueAt).toISOString().slice(0,10) : ''} 
                    onChange={(e) => handleField('dueAt', e.target.value ? new Date(e.target.value).toISOString() : null)}
                    disabled={isCoachView}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-succession-text-primary">Assignee</label>
                  <input 
                    className="w-full border border-succession-border bg-succession-bg-primary text-succession-text-primary rounded-md px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed" 
                    value={edited.assigneeUserId || ''} 
                    onChange={(e) => handleField('assigneeUserId', e.target.value)} 
                    placeholder="User ID or email"
                    disabled={isCoachView}
                  />
                </div>
              </div>
            </div>

            {/* Right - metadata */}
            <div className="w-full md:w-80 border-t md:border-t-0 md:border-l p-6 space-y-4 bg-succession-bg-secondary border-succession-border flex-shrink-0 md:sticky md:top-0 md:self-start">
              <div>
                <div className="text-sm font-medium text-succession-text-primary mb-1">Info</div>
                <div className="text-xs text-succession-text-secondary space-y-2">
                  <div className="flex justify-between"><span>ID</span><span className="font-mono">{task.id}</span></div>
                  <div className="flex items-center justify-between gap-2">
                    <span>Domain</span>
                    {(() => {
                      const d = task.domain || task.taskTemplate?.domain;
                      const cls = d === 'business' ? 'bg-succession-success-bg text-succession-success border-emerald-200' : 'bg-succession-primary-10 text-succession-primary border-blue-200';
                      const label = d ? d.charAt(0).toUpperCase() + d.slice(1) : '—';
                      return <span className={`px-2 py-0.5 rounded-full border text-[11px] ${cls}`}>{label}</span>;
                    })()}
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span>VMI Category</span>
                    {(() => {
                      const v = task.cepaCategory || task.taskTemplate?.cepaCategory;
                      const cls = v === 'identify' ? 'bg-succession-primary-10 text-succession-primary border-blue-200' :
                                 v === 'protect' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                 v === 'build' ? 'bg-purple-50 text-purple-700 border-purple-200' :
                                 v === 'harvest' ? 'bg-orange-50 text-orange-700 border-orange-200' : 'bg-succession-bg-secondary text-succession-text-secondary border-gray-200';
                      const label = v ? v.charAt(0).toUpperCase() + v.slice(1) : '—';
                      return <span className={`px-2 py-0.5 rounded-full border text-[11px] ${cls}`}>{label}</span>;
                    })()}
                  </div>
                <div className="flex items-center justify-between gap-2">
                  <span>Sprint</span>
                  {(() => {
                    // For coach-led sprint programs, show the program name
                    if (sprintProgramName) {
                      return (
                        <span 
                          className="text-succession-text-primary font-medium text-right truncate" 
                          title={sprintProgramName}
                          style={{ maxWidth: '200px' }}
                        >
                          {sprintProgramName}
                        </span>
                      );
                    }
                    // For regular sprints, show sprint names/links
                    const names = Array.isArray(task.sprintNames) ? task.sprintNames : [];
                    const ids = Array.isArray(task.sprintIds) ? task.sprintIds : [];
                    if (!names.length) return <span>Unassigned</span>;
                    return (
                      <div className="flex flex-wrap gap-1 justify-end">
                        {names.map((n, idx) => {
                          const sid = ids[idx];
                          const href = sid ? `/succession/sprint?sprintId=${encodeURIComponent(sid)}` : '/succession/sprint';
                          return (
                            <a key={`modal-sp-${idx}`} href={href} className="text-[11px] rounded-full border px-2 py-0.5 bg-succession-bg-secondary text-succession-text-secondary hover:text-succession-primary hover:border-succession-primary truncate" style={{ maxWidth: '180px' }} title={n}>{n}</a>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
                  {task.taskTemplate?.deadlineInDays ? (
                    <div className="flex justify-between"><span>Deadline</span><span>{task.taskTemplate.deadlineInDays} days</span></div>
                  ) : null}
                </div>
              </div>

              {error && (
                <div className="text-xs text-red-600">{error}</div>
              )}

              {/* Show review buttons or regular action buttons */}
              {isCoachView && isPendingReview ? (
                <div className="pt-2 border-t border-succession-border">
                  <div className="text-xs text-succession-text-secondary mb-2">
                    Use the review buttons above to approve or reject this task.
                  </div>
                </div>
              ) : (
                <div className="pt-2 border-t border-succession-border flex items-center justify-between gap-2">
                  <button 
                    className="px-3 py-2 text-sm border border-succession-border rounded text-succession-text-primary disabled:opacity-50 disabled:cursor-not-allowed" 
                    onClick={handleDelete} 
                    disabled={isSaving || isCoachView}
                  >
                    Delete
                  </button>
                  <div className="flex items-center gap-2">
                    <button 
                      className={`px-3 py-2 text-sm rounded text-white ${hasChanges ? 'bg-succession-primary hover:bg-succession-primary/90' : 'bg-gray-500 cursor-not-allowed'} disabled:opacity-50 disabled:cursor-not-allowed`} 
                      onClick={handleSave} 
                      disabled={!hasChanges || isSaving || isCoachView}
                    >
                      {isSaving ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskDetailModal;


